// Multi-currency formatting for embeddable components

export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'MXN',
] as const

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number]

// Locale mapping for currency-appropriate formatting.
// For dollar-symbol currencies (AUD/CAD/MXN), we deliberately use en-US
// instead of their local locale so the rendered symbol disambiguates from
// USD — e.g. `A$73.45` instead of `$73.45`. Tenants on a global SaaS need
// customers to know which dollar they're being charged.
const CURRENCY_LOCALE_MAP: Record<string, string> = {
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  AUD: 'en-US',
  CAD: 'en-US',
  JPY: 'ja-JP',
  MXN: 'en-US',
}

/**
 * Format a price amount with currency symbol using Intl.NumberFormat.
 * The locale param controls number formatting (decimal/grouping separators).
 * Falls back to currency's default locale if none provided.
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string = 'USD',
  locale?: string
): string {
  const resolvedLocale = locale || CURRENCY_LOCALE_MAP[currency] || 'en-US'
  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(amount)
  } catch {
    // Fallback for unsupported currency codes
    return `${currency} ${amount.toFixed(currency === 'JPY' ? 0 : 2)}`
  }
}

/**
 * Check if a currency code is supported.
 */
export function isSupportedCurrency(code: string): code is SupportedCurrency {
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code)
}
